import { computed, onUnmounted, watch } from '@vue/composition-api';

/**
 * ライフサイクルの期間だけstyleを埋め込む副作用composable。
 *
 * - 閉じたコンポーネントの深い領域のstyleを無理やり上書きしたい。
 * - その際、value値などに **任意の値** を適用したい。
 *
 * というケースで要望を満たすことができる。`useId`と併用する。
 * 任意の値を埋め込む必要がないのであれば、`useCss`ではなく、styleブロックでやりくりすること。
 *
 * **文字列はサニタイズされないことに注意する。API経由で取得されるような値は必ずチェックすること。**
 *
 * 具体的には以下のように使用する。
 *
 * @example
 * ```
 * <script lang="ts">
 * import useCss from '@berlin-front/common/composables/useCss';
 * import useId from '@composables/useId';
 *
 * export default defineComponent({
 *   name: 'MySpecialComponent',
 *   setup(props) {
 *     // 1. インスタンス固有のidを発行。
 *     const id = useId();
 *
 *     // 2. BEMっぽくComponent名でclassを切って、idをmodifierとして使用する、
 *     const className = `MySpecialComponent--${id}`;
 *
 *     // 3. そのクラス名を埋め込んだcssをuseCssを使用して記述する。
 *     //    `props.color`などreactiveな値の使用も可能。
 *     useCss(() => `
 *       .${className} .some-component .deep-class {
 *         background-color: ${props.color};
 *       }
 *     `);
 *
 *     return {
 *       className,
 *     };
 *   }
 * });
 * </script>
 *
 * <template>
 *   <!-- 4. テンプレート内で、そのclass名を使用する。 -->
 *   <div :class="className">
 *     <some-component />
 *   </div>
 * </template>
 * ```
 */
export default (cssFunction: () => string) => {
  const style = computed(cssFunction);
  const styleElement = document.createElement('style');
  document.head.appendChild(styleElement);
  watch(
    [style],
    ([text]) => {
      styleElement.innerHTML = text;
    },
    { immediate: true },
  );
  onUnmounted(() => {
    document.head.removeChild(styleElement);
  });
};
